import { arrayHasLength } from "../../../../../Common/TypingUtils";
import { BindingPath } from "../CoreTypes";
import {
    AbsoluteModelPath,
    createFromMask,
    isModelPath,
    ModelPath,
    PathTokens,
} from "../../../../../Common/ModelPath/ModelPath";
import { MatchKind } from "../../../../../Common/ModelPath/PathMatcher";
import { ModelPathBuilder } from "../../../../../Common/ModelPath/ModelPathBuilder";

export class PathUtils {
    public static getAbsolutePath(parentPath: string, relativePath: string): string {
        const parentSymbol = "-";

        let pathSuffix = relativePath;
        let parentLength = parentPath.length;
        let i = 0;

        while (i < pathSuffix.length && pathSuffix[i] === parentSymbol) {
            if (parentLength === -1) {
                throw new Error("Argument exception");
            }
            parentLength = parentPath.lastIndexOf("/", parentLength - 1);
            i += 1;
        }
        parentPath = parentPath.substr(0, parentLength);
        pathSuffix = pathSuffix.substr(i);

        return (
            (parentPath.length && pathSuffix.length && pathSuffix[0] !== "." ? parentPath + "/" : parentPath) +
            pathSuffix
        );
    }

    public static getParentPath(nodePath: string): null | string {
        const delimIdx = nodePath.lastIndexOf("/");

        return delimIdx === -1 ? null : nodePath.substr(0, delimIdx);
    }

    public static splitToPathAndValueName(path: string): [string, string] {
        for (let i = path.length - 1; i >= 0; --i) {
            if (path[i] === "/") {
                return [path, ""];
            } else if (path[i] === ".") {
                return [path.substr(0, i), path.substr(i + 1)];
            }
        }
        return [path, ""];
    }

    public static getLastToken(nodePath: string): string {
        const delimIdx = nodePath.lastIndexOf("/");

        return delimIdx === -1 ? nodePath : nodePath.substr(delimIdx + 1);
    }

    public static getValuePath(nodePath: BindingPath, valueName: string): string {
        return nodePath + "." + valueName;
    }

    public static isMask(path: string): boolean {
        return path.includes("*");
    }

    public static trimLastStarIfLastToken(path: string | ModelPath): string {
        if (isModelPath(path)) {
            return path.trimLastStarIfLastToken().toLegacyPath();
        }
        if (PathUtils.getLastToken(path) === "*") {
            return PathUtils.getParentPath(path) ?? "";
        }
        return path;
    }

    public static isPrefix(prefix: string, path: string): boolean {
        return path.indexOf(prefix) === 0 && (path.length === prefix.length || path[prefix.length] === "/");
    }

    public static split(path: string): string[] {
        return path.split("/");
    }

    public static splitMask(mask: string): string[] {
        return mask.split("*").map(subPath => this.join(this.split(subPath).filter(x => x)));
    }

    public static join(tokens: string[]): string {
        return tokens.join("/");
    }

    public static resolve(pathWithMask: BindingPath, instances?: string[]): BindingPath;
    public static resolve(pathWithMask: ModelPath, instances?: string[]): ModelPath;
    public static resolve(pathWithMask: AbsoluteModelPath, instances?: string[]): AbsoluteModelPath;
    public static resolve(pathWithMask: BindingPath | ModelPath, instances: string[] = []): BindingPath | ModelPath {
        if (typeof pathWithMask === "string") {
            let instanceIndex = 0;
            const tokens = this.split(pathWithMask);
            const resolvedTokens = tokens.map(token => {
                if (token !== "*") {
                    return token;
                }
                const instance = instances[instanceIndex];
                instanceIndex++;
                if (instance === undefined) {
                    return "*";
                }

                return instance;
            });

            return this.join(resolvedTokens);
        } else {
            const resultBuilder = new ModelPathBuilder(pathWithMask.isAbsolute());
            let instanceIndex = 0;
            for (const token of pathWithMask.getPathParts()) {
                if (PathTokens.isSimpleToken(token)) {
                    resultBuilder.append(token);
                } else {
                    const instance = instances[instanceIndex];
                    instanceIndex++;
                    if (instance === undefined) {
                        resultBuilder.append(token);
                    } else {
                        resultBuilder.append(instance);
                    }
                }
            }

            if (pathWithMask.isAbsolute()) {
                return resultBuilder.toAbsolutePath();
            }
            return resultBuilder.toPath();
        }
    }

    public static resolveMaskToDeepestCommonInstance(fullPath: string, mask: string): string | null {
        const fullPathTokens = this.split(fullPath);
        const maskTokens = this.split(mask);

        const deepestCommonInstancePathTokens = [];
        let tokensBeforeLastMultiplicity = [];

        for (let index = 0; maskTokens[index] && fullPathTokens[index]; index++) {
            if (maskTokens[index] === fullPathTokens[index]) {
                tokensBeforeLastMultiplicity.push(fullPathTokens[index]);
            } else if (maskTokens[index] === "*") {
                const instanceNumber = fullPathTokens[index];
                deepestCommonInstancePathTokens.push(...tokensBeforeLastMultiplicity, instanceNumber);
                tokensBeforeLastMultiplicity = [];
            } else {
                break;
            }
        }
        const deepestCommonInstancePathTokensLength = deepestCommonInstancePathTokens.length;
        if (deepestCommonInstancePathTokensLength === 0) {
            return null;
        }
        return deepestCommonInstancePathTokens
            .concat(maskTokens.slice(deepestCommonInstancePathTokensLength))
            .join("/");
    }

    public static isPicklistFieldValueName(valueName: string): boolean {
        return valueName.startsWith("picklist@");
    }

    public static getPicklistFieldName(valueName: string): string {
        if (!valueName.startsWith("picklist@")) {
            throw new Error(`${valueName} is not picklist field name`);
        }
        const splitValueName = valueName.split("@");
        if (!arrayHasLength(splitValueName, 2)) {
            throw new Error(`${valueName} is not a valid picklist field name`);
        }
        return splitValueName[1];
    }

    /**
     * Используется снаружи. Извлкается номер инстанса для пути. Например, если передать `pathWithMask` /A/B*, а
     * `path` /A/B/2, то функция вернёт `"2"`
     * @param pathWithMask
     * @param path
     */
    public static extractInstance(pathWithMask: BindingPath, path: BindingPath): string {
        const modelPathWithMask = createFromMask(pathWithMask, true, PathTokens.each);
        const modelPath = createFromMask(path, true, PathTokens.each);
        const matchedMultiplicity = modelPathWithMask
            .matchFor(modelPath)
            .filter(x => x.kind === MatchKind.MatchedMultiplicity && PathTokens.isInstanceToken(x.target));

        if (arrayHasLength(matchedMultiplicity, 1) && matchedMultiplicity[0].kind === MatchKind.MatchedMultiplicity) {
            if (PathTokens.isInstanceToken(matchedMultiplicity[0].target)) {
                return matchedMultiplicity[0].target;
            }
        }
        throw new Error(
            `Unable to extract single index while match paths '${modelPathWithMask.toString()}' and '${modelPath.toString()}'`
        );
    }

    public static extractPathWithInstance(pathWithMask: BindingPath, path: BindingPath): string | undefined {
        const prefix = PathUtils.trimLastStarIfLastToken(pathWithMask);
        if (!PathUtils.isPrefix(prefix, path)) {
            return undefined;
        }
        const instance = PathUtils.extractInstance(pathWithMask, path);
        const pathWithInstance = PathUtils.join([prefix, instance]);
        return pathWithInstance;
    }
}
